// @ts-nocheck
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Session } from "@supabase/supabase-js";

interface QueryLimitsState {
  isLoading: boolean;
  queryCount: number;
  maxQueries: number;
  hasReachedLimit: boolean;
  isSubscribed: boolean;
  isTrialing: boolean;
  error: string | null;
}

const FREE_TIER_QUERY_LIMIT = 5;

export function useQueryLimits(session?: Session | null) {
  const { toast } = useToast();
  const [state, setState] = useState<QueryLimitsState>({
    isLoading: true,
    queryCount: 0,
    maxQueries: FREE_TIER_QUERY_LIMIT,
    hasReachedLimit: false,
    isSubscribed: false,
    isTrialing: false,
    error: null,
  });

  // Function to check if user has reached query limit
  const checkQueryLimits = async (userId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      // Check subscription status
      const { data: subscriptionData, error: subscriptionError } = await supabase.functions.invoke('check-subscription', {});
      
      if (subscriptionError) {
        throw new Error(subscriptionError.message);
      }

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('subscription_status, trial_end_date')
        .eq('id', userId)
        .maybeSingle();
      
      if (profileError) {
        throw new Error(profileError.message);
      }
      
      // Fix for null profile issue: Add null check
      if (!profile) {
        console.warn('[useQueryLimits] Profile data is null, using default values');
        setState({
          isLoading: false,
          queryCount: 0,
          maxQueries: FREE_TIER_QUERY_LIMIT,
          hasReachedLimit: false,
          isSubscribed: false,
          isTrialing: false,
          error: null,
        });
        return;
      }

      const isTrialing = profile.subscription_status === 'trialing';
      const isSubscribed = profile.subscription_status === 'active';
      
      // If user is subscribed or still in trial, they have unlimited queries
      if (isSubscribed || isTrialing) {
        setState({
          isLoading: false,
          queryCount: 0,
          maxQueries: Infinity,
          hasReachedLimit: false,
          isSubscribed,
          isTrialing,
          error: null,
        });
        return;
      }
      
      // For free users, check the daily query count
      const today = new Date().toISOString().split('T')[0];
      
      const { data: limitData, error: limitError } = await supabase
        .from('user_query_limits')
        .select('query_count')
        .eq('profile_id', userId)
        .eq('query_date', today)
        .maybeSingle();
      
      if (limitError && limitError.code !== 'PGRST116') { // Not found error
        throw new Error(limitError.message);
      }
      
      const queryCount = limitData?.query_count || 0;
      const hasReachedLimit = queryCount >= FREE_TIER_QUERY_LIMIT;
      
      setState({
        isLoading: false,
        queryCount,
        maxQueries: FREE_TIER_QUERY_LIMIT,
        hasReachedLimit,
        isSubscribed,
        isTrialing,
        error: null,
      });
    } catch (error) {
      console.error('[useQueryLimits] Error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to check query limits',
      }));
    }
  };

  // Function to increment query count
  const incrementQueryCount = async (userId: string) => {
    try {
      if (state.isSubscribed || state.isTrialing) {
        // No need to track for subscribed or trial users
        return true;
      }
      
      if (state.hasReachedLimit) {
        return false;
      }
      
      const today = new Date().toISOString().split('T')[0];
      
      // Try to update existing record
      const { data: updateData, error: updateError } = await supabase
        .from('user_query_limits')
        .update({ query_count: state.queryCount + 1 })
        .eq('profile_id', userId)
        .eq('query_date', today)
        .select('query_count')
        .maybeSingle();
      
      // If record doesn't exist, insert a new one
      if (updateError && updateError.code === 'PGRST116') {
        const { data: insertData, error: insertError } = await supabase
          .from('user_query_limits')
          .insert({ 
            profile_id: userId, 
            query_date: today, 
            query_count: 1 
          })
          .select('query_count')
          .maybeSingle();
        
        if (insertError) {
          throw new Error(insertError.message);
        }
        
        const newQueryCount = insertData?.query_count || 1;
        setState(prev => ({
          ...prev,
          queryCount: newQueryCount,
          hasReachedLimit: newQueryCount >= FREE_TIER_QUERY_LIMIT,
        }));
        
        return true;
      } else if (updateError) {
        throw new Error(updateError.message);
      }
      
      const newQueryCount = updateData?.query_count || state.queryCount + 1;
      setState(prev => ({
        ...prev,
        queryCount: newQueryCount,
        hasReachedLimit: newQueryCount >= FREE_TIER_QUERY_LIMIT,
      }));
      
      // Show toast when approaching limit
      if (newQueryCount === FREE_TIER_QUERY_LIMIT - 1) {
        toast({
          title: "Almost at your daily limit",
          description: `You have 1 free query remaining today.`,
        });
      }
      
      // Return false if user has reached the limit after this increment
      return newQueryCount < FREE_TIER_QUERY_LIMIT;
    } catch (error) {
      console.error('[useQueryLimits] Error incrementing query count:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update query count. Please try again.",
      });
      return false;
    }
  };

  // Initial check when the hook is mounted
  useEffect(() => {
    if (session?.user?.id) {
      checkQueryLimits(session.user.id);
    }
  }, [session?.user?.id]);

  return {
    ...state,
    checkQueryLimits: session?.user ? () => checkQueryLimits(session.user.id) : () => null,
    incrementQueryCount: session?.user ? () => incrementQueryCount(session.user.id) : () => Promise.resolve(false),
    remainingQueries: Math.max(0, FREE_TIER_QUERY_LIMIT - state.queryCount),
  };
} 